import { PaperContent } from '@e3dc-react/shell/Elements/Paper'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Paper, Typography, Button, Grid } from '@mui/material'
import { Stack } from '@mui/system'
import React, { useEffect } from 'react'
import { Control, UseFormSetError, UseFormClearErrors, useController, useWatch } from 'react-hook-form'
import { transformAbbreviatedToWeekdays, transformWeekdaysToAbbreviated } from 'src/Libs/flow/advanced-settings/utils'
import { BoostConfig, WeekDays } from 'src/Libs/flow/installation/forms'
import WeekDaySelector from 'src/Elements/Selectors/WeekDaySelector'
import { UnitTypes } from 'src/Libs/types'
import { makeStyles } from 'tss-react/mui'
import { Day } from '@e3dc-react/shell/Libs/date/types'
import { NumberField, TimePicker } from 'src/Libs/inputs/react-hook-form'
import { transformDateInput } from 'src/Libs/flow/utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

const useStyles = makeStyles()(theme => ({
  paper: {
    paddingInline: theme.spacing(4),
    paddingBlock: theme.spacing(2),
    backgroundColor: theme.palette.shades.shade04,
  },
  settingsStack: {
    gap: theme.spacing(1),
  },
  weekdayStack: {
    gap: 0,
  },
  weekdayHeaderStack: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  noWeekdaySelectedText: {
    px: theme.spacing(1),
    color: theme.palette.error.main,
  },
  settingsGrid: {
    paddingInlineEnd: theme.spacing(2),
  },
  numberFieldInput: {
    '& input': {
      textAlign: 'right',
    },
  },
}))

//#region Boiler Entry
interface BoilerScheduleEntryProps {
  index: number
  control: Control<{ boostConfig: BoostConfig }, unknown>
  setError: UseFormSetError<{ boostConfig: BoostConfig }>
  clearErrors: UseFormClearErrors<{ boostConfig: BoostConfig }>

  disabledDays: WeekDays | undefined
  disableDeleteButton: boolean
  onRemove: () => void
}

/** Form content to add an entry to the boiler schedule - with weekdays-bubbles, 'from'- & 'to'-timepickers and a temperature input  */
export const BoilerScheduleEntry: React.FC<BoilerScheduleEntryProps> = ({
  control,
  index,
  setError,
  clearErrors,
  disabledDays,
  disableDeleteButton,
  onRemove,
}) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()
  const { classes } = useStyles()

  const { field } = useController({
    control,
    name: `boostConfig.${index}.weekDays`,
  })
  const weekdays = useWatch({ control, name: `boostConfig.${index}.weekDays` })
  const noWeekdaySelected = Object.values(weekdays).every(day => day === false)

  useEffect(() => {
    if (noWeekdaySelected) {
      setError(`boostConfig.${index}.weekDays`, { type: 'boostConfig-error' })
    } else {
      clearErrors(`boostConfig.${index}.weekDays`)
    }
  }, [noWeekdaySelected, setError, clearErrors, index])

  const handleDaySelect = (dayName: Day, selected: boolean): void => {
    const updatedDays = transformAbbreviatedToWeekdays({
      ...transformWeekdaysToAbbreviated(field.value),
      [dayName]: selected,
    })
    field.onChange({
      ...field.value,
      ...updatedDays,
    })
  }

  const getDisabledDaysArray = (): Day[] => {
    if (!disabledDays) return []

    const abbreviatedDays = transformWeekdaysToAbbreviated(disabledDays)

    return Object.entries(abbreviatedDays)
      .filter(([_, isDisabled]) => isDisabled)
      .map(([dayName]) => dayName as Day)
  }

  return (
    <Paper className={classes.paper}>
      <PaperContent sxStyle={{ padding: 0 }}>
        <Stack className={classes.settingsStack}>
          <Stack className={classes.weekdayStack}>
            <Stack className={classes.weekdayHeaderStack}>
              <Typography variant={'body1'}>
                {t('user-settings.boiler-page.editor-dialog.title')} {index + 1}
              </Typography>
              <Button variant="text" onClick={onRemove} disabled={!userCanEdit || disableDeleteButton}>
                {t('common.delete')}
              </Button>
            </Stack>
            <WeekDaySelector
              days={transformWeekdaysToAbbreviated(weekdays)}
              onSelectDay={handleDaySelect}
              disabledDays={getDisabledDaysArray()}
              disabled={!userCanEdit}
            />
            {noWeekdaySelected && (
              <Typography variant="caption" className={classes.noWeekdaySelectedText}>
                {t('installation.tariff.missingDay')}
              </Typography>
            )}
          </Stack>
          <Grid container className={classes.settingsGrid}>
            <Grid item xs={12}>
              <NumberField
                name={`boostConfig.${index}.securityTemperature`}
                label={t('user-settings.boiler-page.calendar.security-temperature')}
                suffix={UnitTypes.celsius}
                disabled={!userCanEdit}
                precision={1}
                min={0}
                max={100}
                className={classes.numberFieldInput}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker
                name={`boostConfig.${index}.fromTime`}
                label={t('common.from')}
                disabled={!userCanEdit}
                fullWidth
                onTransform={transformDateInput}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker
                name={`boostConfig.${index}.toTime`}
                label={t('common.to')}
                disabled={!userCanEdit}
                fullWidth
                onTransform={transformDateInput}
              />
            </Grid>
          </Grid>
        </Stack>
      </PaperContent>
    </Paper>
  )
}
//#endregion
